import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineUsers, HiOutlineSearch, HiChevronDown } from "react-icons/hi";
import UserAvatar from "../../../atoms/UserAvatar";
import { useGetSearchUsers } from "../../../../hooks/users";
import { PickedUser, mapSearchUser } from "./AssigneePicker";
import { PERSON_SEARCH_MIN_LENGTH } from "../../../molecules/PersonSearch";

interface Props {
  customerId: string;
  entityId: string;
  selectedIds: string[];
  onAdd: (person: PickedUser) => void;
  label?: string;
  showCount?: boolean;
  filterPerson?: (person: PickedUser) => boolean;
  inline?: boolean;
  align?: "left" | "auto";
}

const clippingBox = (element: HTMLElement): { left: number; right: number } => {
  for (let node = element.parentElement; node; node = node.parentElement) {
    if (getComputedStyle(node).overflowX !== "visible")
      return node.getBoundingClientRect();
  }
  return { left: 0, right: document.documentElement.clientWidth };
};

const PersonFilter: React.FC<Props> = ({
  customerId,
  entityId,
  selectedIds,
  onAdd,
  label,
  showCount = true,
  filterPerson,
  inline = false,
  align = "left",
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || align !== "auto" || !rootRef.current || !panelRef.current)
      return;
    const trigger = rootRef.current.getBoundingClientRect();
    const width = panelRef.current.offsetWidth;
    const box = clippingBox(rootRef.current);
    if (trigger.left + width <= box.right) setPlacement({});
    else if (trigger.right - width >= box.left) setPlacement({ right: 0 });
    else
      setPlacement({
        left: box.left - trigger.left,
        width: box.right - box.left,
      });
  }, [open, align]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const { data: searchData, isFetching } = useGetSearchUsers({
    customer_identification: customerId,
    entityIdentification: entityId,
    query,
    limit: 8,
    pageParam: 0,
  });

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const results: PickedUser[] = useMemo(() => {
    const raw = Array.isArray(searchData)
      ? searchData
      : searchData?.users ?? [];
    return raw
      .map(mapSearchUser)
      .filter(
        (u: PickedUser) =>
          u.id && !selected.has(u.id) && (!filterPerson || filterPerson(u))
      );
  }, [searchData, selected, filterPerson]);

  const searchField = (
    <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-gray-200 mb-1">
      <HiOutlineSearch size={16} className="text-gray-400 shrink-0" />
      <input
        type="text"
        autoFocus={!inline}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t("todos.filterPerson.placeholder") as string}
        className="w-full border-0 p-0 text-sm focus:ring-0 placeholder:text-gray-400"
      />
    </div>
  );

  const typed = query.trim().length;
  const resultList = (
    <div className="max-h-56 overflow-y-auto">
      {typed === 0 ? (
        <div className="px-2 py-2 text-xs text-gray-400">
          {t("todos.filterPerson.hint")}
        </div>
      ) : typed < PERSON_SEARCH_MIN_LENGTH ? (
        <div className="px-2 py-2 text-xs text-gray-400">
          {t("personSearch.description")}
        </div>
      ) : isFetching && results.length === 0 ? (
        <div className="px-2 py-2 text-xs text-gray-400">
          {t("todos.filterPerson.searching")}
        </div>
      ) : searchData?.tooManyResults ? (
        <div className="px-2 py-2 text-xs text-gray-500">
          <span className="block font-medium text-gray-700">
            {t("dashboard.searchPage.tooMany.title")}
          </span>
          {t("dashboard.searchPage.tooMany.description")}
        </div>
      ) : results.length === 0 ? (
        <div className="px-2 py-2 text-xs text-gray-400">
          {t("todos.filterPerson.noResults")}
        </div>
      ) : (
        results.map((u) => (
          <button
            key={u.id}
            type="button"
            onClick={() => {
              onAdd(u);
              setQuery("");
            }}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-gray-50"
          >
            <UserAvatar
              firstName={u.firstName}
              lastName={u.lastName}
              email={u.email}
              avatar={u.avatar}
              sizeClass="w-6 h-6"
              textClass="text-xs"
            />
            <span className="min-w-0">
              <span className="block text-sm text-gray-700 truncate">
                {u.name}
              </span>
              {u.email && (
                <span className="block text-xs text-gray-400 truncate">
                  {u.email}
                </span>
              )}
            </span>
          </button>
        ))
      )}
    </div>
  );

  if (inline) {
    return (
      <div className="w-full">
        {searchField}
        {resultList}
      </div>
    );
  }

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
          open || (showCount && selectedIds.length > 0)
            ? "bg-blue-50 border-blue-300 text-blue-600"
            : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50"
        }`}
      >
        <HiOutlineUsers size={16} />
        {label ?? t("todos.filterPerson.label")}
        {showCount && selectedIds.length > 0 && (
          <span className="ml-0.5 text-xs">({selectedIds.length})</span>
        )}
        <HiChevronDown
          size={14}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          ref={panelRef}
          className="absolute z-40 mt-1 w-72 rounded-lg border border-gray-200 bg-white shadow-lg p-2"
          style={placement}
        >
          {searchField}
          {resultList}
        </div>
      )}
    </div>
  );
};

export default PersonFilter;
